import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Timer,
  Zap,
  CalendarClock,
  PhoneCall,
  ShieldCheck,
  Star,
  Plane,
  Truck,
  Container,
} from "lucide-react";
import { Reveal, MaskedLines, MonoLabel } from "../components/Reveal";
import Marquee from "../components/Marquee";
import TransitCalculator from "../components/TransitCalculator";
import QuoteForm from "../components/QuoteForm";
import {
  services,
  pricing,
  testimonials,
  IMAGES,
  PHONE_DISPLAY,
  PHONE_TEL,
} from "../data/services";

const yesWeDo = [
  { icon: Timer, title: "2-hour pickup", text: "The vast majority of expedited pickups are made within two hours of your call." },
  { icon: Zap, title: "Pricing in minutes", text: "Immediate, accurate pricing on the first call — no waiting, no call-backs hours later." },
  { icon: CalendarClock, title: "7 days a week", text: "Any time of day, any day of the year. We pick up and deliver when you wish, nationwide." },
  { icon: PhoneCall, title: "Humans answer", text: "No call centers and no delays — when you call, the people who own the problem answer." },
  { icon: ShieldCheck, title: "Zero friction", text: "No AI chatbots, no login screens and zero registration required. Ever." },
];

const fleet = [
  { icon: Truck, image: IMAGES.van, name: "Cargo & Sprinter Vans", cap: "1–3 pallets / up to 3,500 lbs", note: "Speed for smaller freight" },
  { icon: Container, image: IMAGES.truck, name: "Straight Trucks", cap: "10–12 pallets / up to 10,000 lbs", note: "Dedicated expedited capacity" },
  { icon: Plane, image: IMAGES.plane, name: "Air Cargo & NFO", cap: "Same day & next day, nationwide", note: "When the road is too slow" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy-deep noise-dark">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={IMAGES.truck}
            alt="Semi truck moving at night on the highway"
            className="h-full w-full object-cover opacity-45"
          />
        </motion.div>
        <div className="absolute inset-0 bg-navy-deep/55" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-navy-deep/80" style={{ maskImage: "linear-gradient(to top, black, transparent)" }} />

        <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 w-full pt-40 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-2 w-2 bg-volt animate-pulse" />
            <p className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-volt">
              Dispatch live — 24/7 · humans answer
            </p>
          </motion.div>

          <MaskedLines
            lines={["URGENT FREIGHT.", "GROUND OR AIR.", "NATIONWIDE. 24/7."]}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[13.5vw] sm:text-[11vw] lg:text-[8.5rem]"
          />

          <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="max-w-xl text-white/70 text-base sm:text-lg leading-relaxed font-medium"
            >
              When time is critical, delays cost more than money. Two-hour
              pickup, immediate pricing on the first call, and a human on the
              line around the clock — no AI, no call centers, no registration.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="flex flex-wrap items-center gap-4"
            >
              <a
                href={PHONE_TEL}
                data-testid="hero-call-button"
                className="flex items-center gap-3 h-14 px-7 bg-volt text-navy-deep font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_rgba(0,229,255,0.35)] hover:shadow-[2px_2px_0px_rgba(0,229,255,0.35)] hover:translate-x-[3px] hover:translate-y-[3px] transition-[box-shadow,transform] duration-150"
              >
                <Phone size={18} strokeWidth={2.5} /> CALL {PHONE_DISPLAY}
              </a>
              <Link
                to="/quote"
                data-testid="hero-quote-button"
                className="flex items-center gap-3 h-14 px-7 border border-white/30 text-white font-display text-lg font-bold tracking-wide hover:bg-white/10 hover:border-white/60 transition-[background-color,border-color] duration-200"
              >
                GET A QUOTE <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-14 grid grid-cols-3 max-w-2xl border-t border-white/15"
          >
            {[
              ["2-HR", "avg pickup"],
              ["24/7", "live humans"],
              ["4.9/5", "shipper rating"],
            ].map(([k, v]) => (
              <div key={k} className="pt-5 pr-6">
                <p className="font-mono text-2xl sm:text-3xl font-bold text-white">{k}</p>
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/45 mt-1">{v}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee
        dark
        items={[
          "2-hour pickup nationwide",
          "sprinter van 600 mi from $1,240",
          "same day air from $935",
          "straight truck 1,000 mi from $3,835",
          "no ai · no call centers · no registration",
          "next day air pallet from $2,630",
        ]}
      />

      {/* YES WE DO */}
      <section data-testid="yeswedo-section" className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-5">
              <MonoLabel>The Way2Go motto</MonoLabel>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
                “Yes<br />we do.”
              </h2>
              <p className="mt-6 text-navy/60 leading-relaxed font-medium max-w-md">
                Freight challenge? We deliver the fastest ground and air
                services for critical freight nationwide — flexible solutions
                and professional service, always.
              </p>
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-blue-900/10 border border-blue-900/10">
              {yesWeDo.map((item, i) => (
                <Reveal key={item.title} delay={i * 0.07} className={i === 4 ? "sm:col-span-2" : ""}>
                  <div className="group h-full bg-white p-8 hover:bg-blue-50/60 transition-colors duration-300">
                    <item.icon size={26} strokeWidth={1.75} className="text-royal group-hover:text-royal-bright transition-colors duration-300" />
                    <h3 className="mt-5 font-display text-xl font-bold text-navy">{item.title}</h3>
                    <p className="mt-2 text-sm text-navy/55 leading-relaxed font-medium">{item.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-[#F5F8FD]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <MonoLabel>What we move</MonoLabel>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
                Services built<br />for the clock
              </h2>
            </div>
            <p className="max-w-sm text-navy/60 font-medium leading-relaxed">
              Every shipment is different, and that is exactly how we treat it —
              consultative options, honest trade-offs, no one-size-fits-all quote.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((s, i) => {
              const spans = [
                "md:col-span-7",
                "md:col-span-5",
                "md:col-span-5",
                "md:col-span-7",
                "md:col-span-7",
                "md:col-span-5",
                "md:col-span-12",
              ];
              return (
                <Reveal key={s.slug} delay={(i % 2) * 0.1} className={spans[i]}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group relative block h-80 sm:h-96 overflow-hidden bg-navy"
                  >
                    <img
                      src={s.image}
                      alt={s.name}
                      className={`absolute inset-0 h-full w-full ${
                        s.contain ? "object-contain" : "object-cover"
                      } object-center opacity-60 group-hover:opacity-75 group-hover:scale-[1.06] transition-[opacity,transform] duration-700 ease-out`}
                    />
                    <div className="absolute inset-0 bg-navy-deep/40 group-hover:bg-navy-deep/20 transition-colors duration-500" />
                    <div className="relative h-full flex flex-col justify-between p-8">
                      <div className="flex items-start justify-between">
                        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-volt bg-navy-deep/60 px-3 py-1.5">
                          {s.tag}
                        </p>
                        <span className="flex h-11 w-11 items-center justify-center bg-white text-navy group-hover:bg-volt transition-colors duration-300">
                          <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                          {s.name}
                        </h3>
                        <p className="mt-3 max-w-md text-sm text-white/70 leading-relaxed font-medium opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-400">
                          {s.blurb}
                        </p>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" data-testid="calculator-section" className="py-24 sm:py-32 bg-navy-deep noise-dark">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 relative z-10">
          <TransitCalculator />
        </div>
      </section>

      {/* PRICING STRIP */}
      <section data-testid="pricing-section" className="py-20 bg-white border-b border-blue-900/10">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <MonoLabel>Sample pricing — call for your exact quote</MonoLabel>
            <a href={PHONE_TEL} data-testid="pricing-call-link" className="font-mono text-sm font-bold text-royal hover:text-royal-bright transition-colors duration-200">
              {PHONE_DISPLAY} →
            </a>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-blue-900/10 border border-blue-900/10">
            {pricing.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.06}>
                <div className="bg-white p-6 h-full hover:bg-blue-50/60 transition-colors duration-300">
                  <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-navy/45 leading-relaxed">
                    {p.label}
                  </p>
                  <p className="mt-3 font-mono text-xl font-bold text-royal">{p.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" data-testid="fleet-section" className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal className="mb-14">
            <MonoLabel>Fleet & modes</MonoLabel>
            <h2 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
              The right vehicle,<br />every time
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fleet.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.1}>
                <div data-testid={`fleet-card-${i}`} className="group border border-blue-900/10 bg-white overflow-hidden">
                  <div className="relative overflow-hidden bg-navy">
                    <img
                      src={f.image}
                      alt={f.name}
                      className="w-full h-auto object-center group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    />
                    <span className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center bg-white">
                      <f.icon size={18} className="text-royal" />
                    </span>
                  </div>
                  <div className="p-7">
                    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-royal-bright">{f.note}</p>
                    <h3 className="mt-2 font-display text-2xl font-bold text-navy">{f.name}</h3>
                    <p className="mt-1 text-sm font-semibold text-navy/50">{f.cap}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "yes we do",
          "ground · air · nationwide",
          "no chatbots — humans answer",
          "pickup within 2 hours",
          "tracked door to door",
          "4.9/5 shipper rating",
        ]}
      />

      {/* TESTIMONIALS */}
      <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-[#F5F8FD]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <MonoLabel>Shipper reviews</MonoLabel>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
                Trust, delivered
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-royal" fill="currentColor" />
              ))}
              <span className="ml-2 font-mono text-sm font-bold text-navy">4.9/5</span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.author} delay={(i % 2) * 0.1}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="h-full bg-white border border-blue-900/10 p-8 sm:p-10 flex flex-col justify-between hover:shadow-[8px_8px_0px_rgba(0,51,160,0.08)] hover:-translate-x-1 hover:-translate-y-1 transition-[box-shadow,transform] duration-300"
                >
                  <blockquote className="font-display text-xl sm:text-2xl font-medium leading-snug text-navy">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-8">
                    <p className="font-bold text-navy">{t.author}</p>
                    <p className="font-mono text-xs tracking-widest uppercase text-navy/45 mt-1">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section id="quote" data-testid="quote-section" className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <MonoLabel>Get moving</MonoLabel>
            <h2 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
              We move fast.<br />So you can too.
            </h2>
            <p className="mt-6 text-navy/60 leading-relaxed font-medium max-w-md">
              Send the details and a live agent calls you back within minutes —
              or skip the form entirely and talk to a human right now.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="quote-call-button"
              className="mt-10 inline-flex items-center gap-4 group"
            >
              <span className="flex h-16 w-16 items-center justify-center bg-royal text-white group-hover:bg-royal-bright transition-colors duration-200">
                <Phone size={24} />
              </span>
              <span>
                <span className="block font-mono text-[11px] tracking-[0.25em] uppercase text-navy/45">
                  Call or chat 24/7
                </span>
                <span className="block font-mono text-2xl font-bold text-royal group-hover:text-royal-bright transition-colors duration-200">
                  {PHONE_DISPLAY}
                </span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
